import { useLingui } from "@lingui/react";
import { CreditCardIcon } from "@phosphor-icons/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import z from "zod";

import type { BillingCheckoutConsentInput } from "@/integrations/multiresume/compliance/consent";

import { ProtectedCapabilityRecovery } from "@/components/auth/protected-capability-recovery";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { resolveActivePaymentProvider } from "@/integrations/multiresume/payment";
import { orpc } from "@/integrations/orpc/client";
import { isSupabaseAuthProvider } from "@/utils/auth-provider";

import { DashboardHeader } from "../-components/header";
import { BillingAccountView } from "./-components/billing-account-view";
import {
  assertNavigableWaffoCheckoutUrl,
  BILLING_REFRESH_CHANNEL,
  BillingCheckoutClientError,
  createBillingCheckoutAttemptController,
  createBillingLiveRefreshController,
  emitOwnedBillingReturnHint,
  isBillingCheckoutTerminalOrderStatus,
  isBillingRefreshHint,
  isDurableBillingCheckoutError,
  type BillingCheckoutAttemptController,
  type BillingCheckoutStatus,
} from "./-components/billing-checkout";
import { getBillingCopy } from "./-components/billing-copy";
import { resolveBillingLoaderResult } from "./-components/billing-loader";
import { BillingCheckoutError, BillingUnavailableState } from "./-components/billing-unavailable-state";

const getBillingClientConfigServerFn = createServerFn({ method: "GET" }).handler(async () => {
  return resolveBillingLoaderResult(resolveActivePaymentProvider());
});

export const Route = createFileRoute("/dashboard/billing/")({
  component: RouteComponent,
  validateSearch: z.object({
    provider_return: z.enum(["success", "cancel"]).optional(),
    order_id: z.uuid().optional(),
  }),
  beforeLoad: ({ context }) => {
    if (!isSupabaseAuthProvider(context.flags.authProvider)) {
      throw redirect({ to: "/dashboard/resumes", replace: true });
    }
  },
  loader: async () => getBillingClientConfigServerFn(),
});

function t(i18n: ReturnType<typeof useLingui>["i18n"], id: string) {
  return getBillingCopy(i18n, id);
}

function RouteComponent() {
  const { i18n } = useLingui();
  const search = Route.useSearch();
  const accountSummaryOptions = useMemo(() => orpc.multiResumeBilling.accountSummary.queryOptions(), []);
  const { data, isPending, isError, isFetching, refetch } = useQuery(accountSummaryOptions);
  const { mutateAsync: createSandboxCheckout, isPending: isCheckoutPending } = useMutation(
    orpc.multiResumeBilling.createSandboxCheckout.mutationOptions(),
  );
  const { mutateAsync: resumePendingCheckout, isPending: isResumePending } = useMutation(
    orpc.multiResumeBilling.resumePendingCheckout.mutationOptions(),
  );
  const [checkoutStatus, setCheckoutStatus] = useState<BillingCheckoutStatus | null>(null);
  const [protectedOperationError, setProtectedOperationError] = useState<unknown>(null);
  const refreshBillingSummary = useCallback(async () => await refetch(), [refetch]);
  const activeOrderIdRef = useRef<string | null>(null);
  const refreshDependenciesRef = useRef({ refreshBillingSummary });
  refreshDependenciesRef.current = { refreshBillingSummary };
  const liveRefreshControllerRef = useRef<ReturnType<typeof createBillingLiveRefreshController> | null>(null);
  if (!liveRefreshControllerRef.current) {
    liveRefreshControllerRef.current = createBillingLiveRefreshController({
      refresh: async () => {
        const result = await refreshDependenciesRef.current.refreshBillingSummary();
        const orderId = activeOrderIdRef.current;
        const order = orderId ? result.data?.orders.find((candidate) => candidate.id === orderId) : null;
        const terminal = !!order && isBillingCheckoutTerminalOrderStatus(order.status);
        if (terminal) {
          activeOrderIdRef.current = null;
          setCheckoutStatus("closed");
        }
        return terminal;
      },
      onTimeout: () => {
        setCheckoutStatus("processing_timeout");
      },
    });
  }
  const liveRefreshController = liveRefreshControllerRef.current;

  useEffect(() => {
    const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(BILLING_REFRESH_CHANNEL);
    if (channel) {
      channel.onmessage = (event) => {
        if (!isBillingRefreshHint(event.data)) return;
        activeOrderIdRef.current = event.data.orderId;
        if (liveRefreshController.signal()) setCheckoutStatus("confirming");
      };
    }
    const handleFocus = () => {
      if (activeOrderIdRef.current) liveRefreshController.signal();
    };
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
      channel?.close();
      liveRefreshController.cancel();
    };
  }, [liveRefreshController]);

  const reconciledProviderReturnRef = useRef<string | null>(null);
  const providerReturnOrder =
    search.provider_return && search.order_id ? data?.orders.find((order) => order.id === search.order_id) : null;
  useEffect(() => {
    if (!providerReturnOrder || reconciledProviderReturnRef.current === providerReturnOrder.id) return;
    reconciledProviderReturnRef.current = providerReturnOrder.id;
    activeOrderIdRef.current = providerReturnOrder.id;
    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel(BILLING_REFRESH_CHANNEL);
      emitOwnedBillingReturnHint(providerReturnOrder.id, data?.orders ?? [], (message) => channel.postMessage(message));
      channel.close();
    }
    if (liveRefreshController.signal()) setCheckoutStatus("confirming");
  }, [data?.orders, liveRefreshController, providerReturnOrder]);

  const checkoutDependenciesRef = useRef({
    createCheckout: createSandboxCheckout,
    reconcilePending: refreshBillingSummary,
  });
  checkoutDependenciesRef.current = { createCheckout: createSandboxCheckout, reconcilePending: refreshBillingSummary };
  const checkoutAttemptControllerRef = useRef<BillingCheckoutAttemptController | null>(null);
  if (!checkoutAttemptControllerRef.current) {
    checkoutAttemptControllerRef.current = createBillingCheckoutAttemptController({
      createIdempotencyKey: (currentPackageId) => `phase6-checkout:${currentPackageId}:${crypto.randomUUID()}`,
      createCheckout: (input) => {
        if (!input.consent) throw new Error("Checkout consent is required.");
        return checkoutDependenciesRef.current.createCheckout({ ...input, consent: input.consent });
      },
      reconcilePending: async () => {
        await checkoutDependenciesRef.current.reconcilePending();
      },
      openBlankCheckoutTab: () => window.open("about:blank", "_blank"),
    });
  }
  const checkoutAttemptController = checkoutAttemptControllerRef.current;

  const handleCreateCheckout = async (packageId: string, consent: BillingCheckoutConsentInput) => {
    setCheckoutStatus("opening");
    setProtectedOperationError(null);

    try {
      const result = await checkoutAttemptController.submit(packageId, consent);
      activeOrderIdRef.current = result.order.id;
      liveRefreshController.start();
      setCheckoutStatus((current) => (current === "confirming" || current === "closed" ? current : "opened"));
    } catch (checkoutError) {
      setCheckoutStatus("error");
      setProtectedOperationError(checkoutError);
      if (checkoutError instanceof BillingCheckoutClientError) {
        if (checkoutError.code === "checkout_url_missing") {
          toast.error(t(i18n, "Checkout was not created. Please refresh billing and try again."));
          await refreshBillingSummary();
          return;
        }
        await refreshBillingSummary();
        return;
      }

      if (isDurableBillingCheckoutError(checkoutError)) {
        toast.error(t(i18n, "Checkout could not be started. Please refresh billing and try again."));
      } else {
        toast.error(t(i18n, "Checkout status is unknown. Retry to resume the same checkout."));
      }
    }
  };

  const handleResumeCheckout = async (orderId: string) => {
    const checkoutTab = window.open("about:blank", "_blank");
    if (!checkoutTab) {
      setCheckoutStatus("error");
      setProtectedOperationError(new BillingCheckoutClientError("Pop-up was blocked.", "popup_blocked"));
      return;
    }
    setCheckoutStatus("opening");
    setProtectedOperationError(null);
    try {
      const result = await resumePendingCheckout({ orderId });
      if (result.provider !== "waffo_pancake" || !result.checkoutUrl) {
        throw new BillingCheckoutClientError("Secure checkout was not created.", "checkout_url_missing");
      }
      const checkoutUrl = assertNavigableWaffoCheckoutUrl(result.checkoutUrl, result.checkoutNavigation);
      checkoutTab.opener = null;
      checkoutTab.location.href = checkoutUrl;
      activeOrderIdRef.current = result.order.id;
      liveRefreshController.start();
    } catch (checkoutError) {
      checkoutTab.close();
      setCheckoutStatus("error");
      setProtectedOperationError(checkoutError);
      toast.error(t(i18n, "Checkout could not be started. Please refresh billing and try again."));
      await refreshBillingSummary();
    }
  };

  const handleUnknownCheckoutRecovery = async () => {
    const attempt = checkoutAttemptController.getAttempt();
    if (!attempt) {
      await refreshBillingSummary();
      return;
    }

    setCheckoutStatus("opening");
    setProtectedOperationError(null);
    try {
      const result = await checkoutAttemptController.submit(attempt.packageId, attempt.consent);
      activeOrderIdRef.current = result.order.id;
      liveRefreshController.start();
      setCheckoutStatus((current) => (current === "confirming" || current === "closed" ? current : "opened"));
    } catch (checkoutError) {
      setCheckoutStatus("error");
      setProtectedOperationError(checkoutError);
    }
  };

  return (
    <div className="space-y-4">
      <DashboardHeader icon={CreditCardIcon} title={t(i18n, "Quota & Billing")} />

      <Separator />

      <ProtectedCapabilityRecovery error={protectedOperationError} safeReturnTarget="/dashboard/billing" />

      {checkoutStatus === "error" && (
        <BillingCheckoutError
          result={isDurableBillingCheckoutError(protectedOperationError) ? "durable" : "unknown"}
          onRetry={() =>
            void (isDurableBillingCheckoutError(protectedOperationError)
              ? refreshBillingSummary()
              : handleUnknownCheckoutRecovery())
          }
          isRetrying={isFetching || isCheckoutPending}
        />
      )}

      {isPending ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]">
          <Skeleton className="h-36" />
          <Skeleton className="h-36" />
          <Skeleton className="h-64 lg:col-span-2" />
        </div>
      ) : isError ? (
        <BillingUnavailableState onRetry={() => void refetch()} isRetrying={isFetching} />
      ) : (
        <BillingAccountView
          summary={data}
          onCreateCheckout={handleCreateCheckout}
          onResumeCheckout={handleResumeCheckout}
          isCheckoutPending={isCheckoutPending || isResumePending}
          checkoutStatus={checkoutStatus}
          onRefreshBilling={async () => {
            const result = await refreshBillingSummary();
            const orderId = activeOrderIdRef.current;
            const order = orderId ? result.data?.orders.find((candidate) => candidate.id === orderId) : null;
            if (order && isBillingCheckoutTerminalOrderStatus(order.status)) {
              activeOrderIdRef.current = null;
              setCheckoutStatus("closed");
            } else {
              setCheckoutStatus("processing_timeout");
            }
          }}
        />
      )}
    </div>
  );
}
